// useMethods.ts

import axios from "axios";
import Cookies from "js-cookie";
import { useParamsContext } from "../context/juno/ParamsContext";
import { useUploadImageContext } from "../context/juno/UploadImageContext";
import { useUpscaleContext } from "../context/juno/UpscaleContext";
import { useVideo } from "../context/juno/VideoContext";
import { AiEngineList, AspectRatio, HistoryParamsType, ImageInfo, Style } from "../types/junoTypes"; // 必要な型をインポート
import { getFileDateName } from "../utils/utils";
import { useCustomNavigate } from "./useCustomNavigate";

export const useJunoMethods = () => {
  const navigate = useCustomNavigate();
  const paramsContext = useParamsContext();
  const upscaleContext = useUpscaleContext();
  const uploadImageContext = useUploadImageContext();
  const useVideoContext = useVideo();

  const handleSetSameParams = (params: HistoryParamsType) => {
    const {
      setPrompt,
      setStep,
      setGuidanceScale,
      setSeed,
      setNegativePrompt,
      setSample,
      setRevisedPrompt,
      setStyle,
      setStyleWeight,
      setAspectRatio,
      setRawMode,
      setColorPalette,
      setColorsRecraft,
      setLoras,
    } = paramsContext;

    // params.ai_engine が undefined の場合のデフォルト値を設定 (例: DallE3)
    const currentAiEngine = params.ai_engine ?? "DallE3";
    const aiEngine = newVersionAiEngine(currentAiEngine) as AiEngineList;

    const setCommonParams = (aiEngine: AiEngineList) => {
      // 幅と高さからアスペクト比を決定する代わりに、直接アスペクト比を使用
      if (
        params.aspect_ratio &&
        ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
      ) {
        setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
      }
      if (params.step) setStep((prev) => ({ ...prev, [aiEngine]: params.step }));
      if (params.guidance_scale) setGuidanceScale((prev) => ({ ...prev, [aiEngine]: params.guidance_scale }));
      if (params.seed) setSeed((prev) => ({ ...prev, [aiEngine]: params.seed }));
      if (params.negative_prompt) setNegativePrompt(params.negative_prompt);
      if (params.sample) {
        setSample((prev) => ({ ...prev, [aiEngine]: params.sample }));
      } else {
        setSample((prev) => ({ ...prev, [aiEngine]: 1 }));
      }
    };

    // params.prompt が undefined の場合のデフォルト値を設定
    setPrompt(params.prompt ?? "");

    switch (aiEngine) {
      case "DallE3":
        // 幅と高さの代わりにアスペクト比を設定
        // 適切なAspectRatio型の値を使用 (AspectRatio = "1:1" | "16:9" | "5:4" | ... | "9:16")
        if (
          params.aspect_ratio &&
          ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
        ) {
          setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
        } else if (params.width && params.height) {
          // 幅と高さからアスペクト比を推定
          if (params.width === 1792 && params.height === 1024) {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: "16:9" as AspectRatio }));
          } else if (params.width === 1024 && params.height === 1792) {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: "9:16" as AspectRatio }));
          } else {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: "1:1" as AspectRatio }));
          }
        }
        // params.style を Style[typeof aiEngine] 型にキャスト
        setStyle((prev) => ({ ...prev, [aiEngine]: params.style as Style[typeof aiEngine] }));
        setRevisedPrompt(false);
        break;

      case "SD35":
      case "SDUltra":
        if (
          params.aspect_ratio &&
          ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
        ) {
          setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
        }
        setCommonParams(aiEngine);
        break;

      default:
        if (aiEngine.includes("Flux")) {
          if (
            params.aspect_ratio &&
            ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
          ) {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
          }
          if (params.raw) setRawMode(params.raw);
          // params.style を Style[typeof aiEngine] 型にキャスト
          if (params.style) setStyle((prev) => ({ ...prev, [aiEngine]: params.style as Style[typeof aiEngine] }));
          if (params.style_weight) setStyleWeight((prev) => ({ ...prev, [aiEngine]: params.style_weight }));
          // lorasパラメータの設定
          if (params.loras && params.loras.length > 0) {
            setLoras((prev) => ({ ...prev, [aiEngine]: params.loras }));
          }
        }
        if (aiEngine.includes("RecraftV3")) {
          if (
            params.aspect_ratio &&
            ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
          ) {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
          }
          // params.style を Style[typeof aiEngine] 型にキャスト
          if (params.style) setStyle((prev) => ({ ...prev, [aiEngine]: params.style as Style[typeof aiEngine] }));
          if (params.colors) setColorsRecraft(params.colors);
        }
        if (["IdeogramV3", "IdeogramV3Remix", "IdeogramV3Edit", "IdeogramV3ReplaceBackground"].includes(aiEngine)) {
          if (
            params.aspect_ratio &&
            ["1:1", "16:9", "9:16", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "2:1"].includes(params.aspect_ratio)
          ) {
            setAspectRatio((prev) => ({ ...prev, [aiEngine]: params.aspect_ratio as AspectRatio }));
          }
          setSeed((prev) => ({ ...prev, [aiEngine]: params.seed }));
          // params.style を Style[typeof aiEngine] 型にキャスト
          if (params.style) setStyle((prev) => ({ ...prev, [aiEngine]: params.style as Style[typeof aiEngine] }));
          if (params.color_palette) setColorPalette(params.color_palette);
        }
        break;
    }

    // params.action が undefined の場合のデフォルト値を設定 (例: txt2img)
    navigate(`/juno/image-generator?menu=${params.action ?? "txt2img"}&ai_engine=${aiEngine}`);
  };

  // AI Engineのバージョンを生成用に最新に変更する
  const newVersionAiEngine = (aiEngine: string | undefined): string => {
    // aiEngine が undefined の場合のデフォルト値を設定
    const currentEngine = aiEngine ?? "DallE3";
    switch (currentEngine) {
      case "Flux1Pro":
        return "Flux11Pro";
      case "SD3":
        return "SD35";
      default:
        // aiEngineではなく、undefinedでないことが保証されているcurrentEngineを返す
        return currentEngine;
    }
  };

  const handleImg2Img = async (image_uuid: string, params: HistoryParamsType) => {
    try {
      const { setUuidParent } = upscaleContext;
      const { setImage } = uploadImageContext;
      handleSetSameParams(params);
      setUuidParent(image_uuid);
      const response = await axios.get(`/api/v1/juno/image/${image_uuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setImage(base64data);
      };
      navigate(`/juno/image-generator?menu=img2img&ai_engine=${params.ai_engine}`);
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
    }
  };

  const handleImg2Vid = async (image_uuid: string, prompt: string) => {
    try {
      const { setImage1, setPrompt, setMenu, setAiEngine } = useVideoContext;
      const response = await axios.get(`/api/v1/juno/image/${image_uuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setImage1(base64data);
        setPrompt(prompt);
        setMenu("i2v");
        setAiEngine("LUMA");
      };
      navigate("/juno/video-generator");
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
    }
  };

  const handleUpscale = async (image_uuid: string) => {
    try {
      const { setUuidParent } = upscaleContext;
      const { setImage } = uploadImageContext;
      setUuidParent(image_uuid);
      const response = await axios.get(`/api/v1/juno/image/${image_uuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setImage(base64data);
      };
      navigate(`/juno/image-generator?menu=upscale&ai_engine=AuraSR`);
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
    }
  };

  const handleEdit = async (image_uuid: string) => {
    try {
      const { setUuidParent } = upscaleContext;
      let image = "";
      const response = await axios.get(`/api/v1/juno/image/${image_uuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });
      setUuidParent(image_uuid);

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        image = base64data;
        navigate(`/juno/image-generator?menu=editor&ai_engine=Flux1DevFill`, { state: { outsideImage: image } });
      };
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
    }
  };

  const handleDownload = async (image_uuid: string) => {
    try {
      const response = await axios.get(`/api/v1/juno/image/${image_uuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });
      const url = window.URL.createObjectURL(blob);

      let extension = ".png"; // デフォルト拡張子
      if (response.data.type === "image/svg+xml") {
        extension = ".svg";
      } else if (response.data.type === "image/jpeg") {
        extension = ".jpg";
      } else if (response.data.type === "image/png") {
        // デフォルトがpngなので明示的なチェックは不要だが、念のため
        extension = ".png";
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = `juno-${getFileDateName()}${extension}`;
      document.body.appendChild(a); // DOMに追加
      a.click();
      document.body.removeChild(a); // DOMから削除

      // リソース解放
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
      return { success: false };
    }
  };

  const fetchHistory = async (): Promise<[ImageInfo][]> => {
    try {
      const url = "/api/v1/juno/generated-images";
      const csrftoken = Cookies.get("csrftoken");
      const headers = {
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      };
      const response = await axios.get(url, { headers });
      const res_json: [ImageInfo][] = response.data;
      return res_json;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const fetchQueuedImages = async (imageRequestUuid: string, action: string): Promise<ImageInfo[]> => {
    try {
      const url = `/api/v1/juno/queued-images?uuid=${imageRequestUuid}&action=${action}`;
      const csrftoken = Cookies.get("csrftoken");

      const headers = {
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      };

      const response = await axios.get(url, { headers });
      const res_json: ImageInfo[] = response.data;
      return res_json;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  return {
    handleSetSameParams,
    handleImg2Img,
    handleUpscale,
    handleDownload,
    fetchHistory,
    fetchQueuedImages,
    handleImg2Vid,
    handleEdit,
    downloadChatGeneratedImage, // 新しい関数をエクスポート
  };
};

// チャットで生成された画像用のダウンロード関数
const downloadChatGeneratedImage = async (image_uuid: string) => {
  try {
    const response = await axios.get(`/api/v1/image-generator/image/${image_uuid}`, { responseType: "blob" });
    const blob = new Blob([response.data], { type: response.data.type });
    const url = window.URL.createObjectURL(blob);

    let extension = ".png"; // デフォルト拡張子
    if (response.data.type === "image/svg+xml") {
      extension = ".svg";
    } else if (response.data.type === "image/jpeg") {
      extension = ".jpg";
    } else if (response.data.type === "image/png") {
      extension = ".png";
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-generated-${getFileDateName()}${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error("画像のダウンロードに失敗しました:", error);
    return { success: false };
  }
};
